/*──────────────────────────────────────────────────────────────
 * src/pages/HomePage.jsx – regenerated 2025-07-25
 *   • HOURS MODAL: duplicated wrapper removed; now uses startDate/endDate
 *     state with a simple updateRange() helper and hoursLoading flag.
 *   • EXPLORER MODAL & BUTTON: rendered only when perms.hasOgFiles === true.
 *   • Safer localStorage perms parse (defaults to {} instead of "false").
 *   • Everything else remains line-for-line identical.
 *──────────────────────────────────────────────────────────────*/

import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, useUser } from "ogcommon";
import debounce from "lodash.debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faEyeSlash,
  faClock,
  faFolderOpen,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ExplorerModal from "../components/ExplorerModal";
import { baseUrl } from "./config";
import { fetchTicketList, sameUser, isBilled } from "../utils/tickets";
import { lsGet, lsSet, lsRemove, lsProbe } from "../utils/storage";

/* Gated, no-secrets diagnostics: ?debugTickets=1 (summary) or =2 (deep, with raw
   stage samples, device/runtime info, DOM/CSS checks, and a raw-fetch test button). */
const DEBUG_TICKETS_LEVEL =
  typeof window !== "undefined"
    ? Number(
        new URLSearchParams(window.location.search).get("debugTickets")
      ) || 0
    : 0;
const DEBUG_TICKETS = DEBUG_TICKETS_LEVEL >= 1;
const APP_VERSION =
  (typeof document !== "undefined" &&
    document.querySelector('meta[name="app-version"]')?.content) ||
  "unknown";

/* Ticket row (lazy) */
const TicketItem = lazy(() => import("./TicketItem"));

/*──────────────── constants ─────────────────────────────────*/
const LS = {
  SHOW_UNBILLED: "hpShowUnbilled",
  QUICK_FILTER: "hpQuickFilter",
  PAGE: "hpCurrentPage",
  TICKETS_CACHE: "hpTicketsCache",
  START_DATE: "hpStartDate",
  END_DATE: "hpEndDate",
};
const ticketsPerPage = 5;
const toISO = (d) => d.toISOString().slice(0, 10);
const todayISO = () => toISO(new Date());
const sevenDaysAgoISO = () => toISO(new Date(Date.now() - 6 * 86_400_000));
const monthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  return toISO(d);
};

/* Safely parse JSON that may be a string, an already-parsed object, null, or
   corrupt. Returns `fallback` instead of throwing — important on mobile, where an
   interrupted login can leave a partial/garbage blob in localStorage. */
const safeParseJson = (value, fallback = null) => {
  if (value == null) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/* Normalized field-ticket write permission. Honors the user-level ACL
   (perms.ogfieldticket.write) OR the client-level UseFieldTicket flag — whichever
   grants access — rather than a single brittle nested lookup. */
const canWriteFieldTicket = (creds = {}) => {
  const p = (creds && creds.perms) || {};
  return !!(p?.ogfieldticket?.write || p?.UseFieldTicket === true);
};

/*──────────────── generic modal (for hours) ─────────────────*/
const Modal = ({ open, onClose, children, theme }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={`relative w-full max-w-3xl mx-auto rounded-2xl shadow-2xl overflow-hidden ${
          theme === "dark" ? "bg-gray-800" : "bg-white"
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-300 hover:text-white text-2xl focus:outline-none z-50"
          aria-label="Close"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
};

/*──────────────── HomePage ─────────────────────────────────*/
const HomePage = () => {
  /* context & routing */
  const { theme } = useTheme();
  const { userRole, userID, companyName } = useUser();
  const navigate = useNavigate();

  /*──────── permissions ─────────*/
  // Load the full credentials object from localStorage. safeParseJson guards
  // against a missing/corrupt blob so a bad value can never crash the render.
  const storedCredentials =
    safeParseJson(lsGet("credentials"), {}) || {};

  // Extract whatever perms shape you have in credentials.perms
  const storedPerms =
    storedCredentials.perms && typeof storedCredentials.perms === "object"
      ? storedCredentials.perms
      : {};

  // Initialize a flat “perms” state (with your defaults) if you still need it
  const [perms, setPerms] = useState(() => ({
    hasOgFiles: false,
    UseFieldTicket: false,
    ...storedPerms,
  }));

  // Field-ticket write access: normalized from the user ACL OR the client
  // UseFieldTicket flag (see canWriteFieldTicket) — not a brittle raw lookup.
  const [writeAccess, setWriteAccess] = useState(() =>
    canWriteFieldTicket(storedCredentials)
  );

  /*──────── ticket state ───────*/
  const [allTickets, setAllTickets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [diag, setDiag] = useState(null); // populated only when DEBUG_TICKETS
  const [domDiag, setDomDiag] = useState(null); // debugTickets=2: real DOM/CSS
  const [rawTest, setRawTest] = useState(null); // debugTickets=2: raw-fetch probe
  const [preSearchCount, setPreSearchCount] = useState(0); // results before search
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(
    Number(lsGet(LS.PAGE)) || 1
  );
  const [showUnbilled, setShowUnbilled] = useState(
    safeParseJson(lsGet(LS.SHOW_UNBILLED), true)
  );
  const [searchQuery, setSearchQuery] = useState(lsGet(LS.QUICK_FILTER) || "");
  const [highestTicketNumber, setHighestTicketNumber] = useState(null);

  /*──────── hours modal ───────*/
  const [startDate, setStartDate] = useState(
    lsGet(LS.START_DATE) || sevenDaysAgoISO()
  );
  const [endDate, setEndDate] = useState(lsGet(LS.END_DATE) || todayISO());
  const [hoursData, setHoursData] = useState([]);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);

  /*──────── explorer modal ─────*/
  const [explorerOpen, setExplorerOpen] = useState(false);

  /*──────── helper: updateRange ─*/
  const updateRange = (sIso, eIso) => {
    setStartDate(sIso);
    setEndDate(eIso);
    lsSet(LS.START_DATE, sIso);
    lsSet(LS.END_DATE, eIso);
  };

  /*──────── tickets fetch logic ─*/
  const debouncedSearchTickets = useMemo(
    () =>
      debounce((q) => {
        setSearchQuery(q);
        lsSet(LS.QUICK_FILTER, q);
        setCurrentPage(1);
      }, 300),
    []
  );

  const fetchTickets = useCallback(async () => {
    // Lightweight list payload (no per-ticket items / image globs) — the decisive
    // fix for real-mobile choke. Detail/hours fetch the full ticket on demand.
    const endpoint = `${baseUrl}/api/tickets.php?summary=1`;
    const dbg = DEBUG_TICKETS
      ? {
          appVersion: APP_VERSION,
          host: window.location.host,
          origin: window.location.origin,
          baseUrl,
          endpoint,
          online: navigator.onLine,
          userID,
          userRole,
          companyName,
          perms: {
            UseFieldTicket: !!perms.UseFieldTicket,
            ogfieldticketRead: !!storedCredentials.perms?.ogfieldticket?.read,
            ogfieldticketWrite: !!storedCredentials.perms?.ogfieldticket?.write,
          },
        }
      : null;
    try {
      let nextId = null;
      let fetched = [];
      let usedCache = false;
      if (navigator.onLine) {
        // Shared, defensive loader. `list` is null on ANY failure (network, HTTP
        // error, HTML/login page, or unexpected JSON shape) — never a misparsed
        // array — so .filter() can't throw and a bad response can't blank the list.
        const { list, error, status, contentType, shape } =
          await fetchTicketList(endpoint);
        if (dbg) {
          dbg.httpStatus = status;
          dbg.contentType = contentType;
          dbg.responseShape = shape;
          dbg.loaderError = error;
        }

        // tickets.php ALWAYS appends an {isNextTicketID:true} marker, so a healthy
        // payload is never zero-length. A bare [] is abnormal (truncated/stale), so
        // treat it as a soft failure and keep the last good cache rather than wipe.
        if (list && list.length > 0) {
          setLoadError("");
          const cleaned = list.filter((t) => {
            if (t.isNextTicketID) {
              nextId = t.Ticket;
              return false;
            }
            return true;
          });
          cleaned.sort(
            (a, b) =>
              b.Ticket - a.Ticket ||
              new Date(b.TicketDate) - new Date(a.TicketDate)
          );
          fetched = cleaned;
          // Best-effort cache writes. iOS Safari throws here in Private mode /
          // on quota — lsSet swallows it so the flow ALWAYS reaches setTickets().
          lsSet(LS.TICKETS_CACHE, JSON.stringify(fetched));
          lsSet("hpTicketsCacheTs", Date.now().toString());

          // Clean up offline-stored tickets that the server now has
          const serverTicketIds = new Set(fetched.map((t) => String(t.Ticket)));
          const offlineTickets = safeParseJson(lsGet("tickets"), []) || [];
          const stillPending = offlineTickets.filter(
            (t) => !serverTicketIds.has(String(t.Ticket))
          );
          lsSet("tickets", JSON.stringify(stillPending));
        } else {
          // Load failed — keep the last good cache instead of blanking. If there's
          // no cache to fall back to, surface a clear error (not a silent blank).
          if (process.env.NODE_ENV !== "production") {
            console.warn("[HomePage] tickets load failed:", error);
          }
          const cached = safeParseJson(lsGet(LS.TICKETS_CACHE), []);
          fetched = Array.isArray(cached) ? cached : [];
          usedCache = fetched.length > 0;
          setLoadError(
            fetched.length
              ? ""
              : "Couldn’t load tickets right now. Check your connection and try again."
          );
        }
      } else {
        usedCache = true;
        const cacheTs = Number(lsGet("hpTicketsCacheTs") || "0");
        const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
        if (Date.now() - cacheTs < MAX_CACHE_AGE_MS) {
          fetched = safeParseJson(lsGet(LS.TICKETS_CACHE), []) || [];
        } else {
          // Stale offline cache — discard to prevent showing ancient tickets
          fetched = [];
        }
      }

      // Merge any offline-stored tickets that aren't yet in the fetched list
      const offlineTickets = safeParseJson(lsGet("tickets"), []) || [];
      if (offlineTickets.length > 0) {
        const fetchedIds = new Set(fetched.map((t) => String(t.Ticket)));
        const pendingTickets = offlineTickets.filter(
          (t) => !fetchedIds.has(String(t.Ticket))
        );
        if (pendingTickets.length > 0) {
          fetched = [...pendingTickets, ...fetched];
        }
      }

      if (!nextId && fetched.length)
        nextId = (Math.max(...fetched.map((t) => +t.Ticket)) + 1).toString();
      setHighestTicketNumber(nextId);
      setAllTickets(fetched);

      // Filters are chained (same AND semantics as before) so each stage can be
      // counted for diagnostics. UserID comparison is normalized (trim/case) and
      // the billed flag is parsed robustly — see utils/tickets.js.
      const afterRoleUser = fetched.filter(
        (t) => userRole !== "P" || sameUser(t.UserID, userID)
      );
      const afterBilled = afterRoleUser.filter((t) => {
        const unbilled = !isBilled(t.Billed);
        return userRole === "P" ? unbilled : showUnbilled ? unbilled : !unbilled;
      });
      const final = searchQuery
        ? afterBilled.filter((t) =>
            Object.values(t).some((v) =>
              String(v).toLowerCase().includes(searchQuery.toLowerCase())
            )
          )
        : afterBilled;

      setPreSearchCount(afterBilled.length);

      if (dbg) {
        const probe = lsProbe();
        dbg.usedCache = usedCache;
        dbg.localStorageWritable = probe.ok;
        dbg.localStorageError = probe.error;
        dbg.rawCount = fetched.length;
        dbg.afterRoleUserCount = afterRoleUser.length;
        dbg.afterBilledCount = afterBilled.length;
        dbg.afterSearchCount = final.length;
        dbg.searchQuery = searchQuery || "(none)";
        dbg.sampleOwners = fetched
          .slice(0, 5)
          .map((t) => JSON.stringify(String(t.UserID ?? "")));
        dbg.ls = {
          page: lsGet(LS.PAGE),
          search: lsGet(LS.QUICK_FILTER),
          showUnbilled: lsGet(LS.SHOW_UNBILLED),
          cacheLen: (safeParseJson(lsGet(LS.TICKETS_CACHE), []) || []).length,
          offlineLen: (safeParseJson(lsGet("tickets"), []) || []).length,
        };
        if (DEBUG_TICKETS_LEVEL >= 2) {
          // Deeper, real-device-only signals (level 2). Safe summaries only.
          const env = typeof window !== "undefined" ? window : {};
          const nav = typeof navigator !== "undefined" ? navigator : {};
          const safeRow = (t) => ({
            id: t.Ticket ?? t.TicketID ?? t.ID ?? null,
            owner: String(t.UserID ?? ""),
            billed: t.Billed ?? null,
            date: t.TicketDate ?? null,
          });
          dbg.env = {
            userAgent: nav.userAgent || "",
            standalone:
              nav.standalone === true ||
              (env.matchMedia &&
                env.matchMedia("(display-mode: standalone)").matches) ||
              false,
            innerWidth: env.innerWidth,
            innerHeight: env.innerHeight,
            devicePixelRatio: env.devicePixelRatio,
            touch: "ontouchstart" in env || (nav.maxTouchPoints || 0) > 0,
            online: nav.onLine,
          };
          dbg.rawSample = fetched.slice(0, 10).map(safeRow);
          dbg.afterRoleUserSample = afterRoleUser.slice(0, 10).map(safeRow);
          dbg.afterBilledSample = afterBilled.slice(0, 10).map(safeRow);
          dbg.afterSearchSample = final.slice(0, 10).map(safeRow);
        }
        setDiag(dbg);
        // eslint-disable-next-line no-console
        console.log("[debugTickets]", dbg);
      }

      setTickets(final);
      setLoading(false);
    } catch (err) {
      console.error("fetchTickets", err);
      if (dbg) {
        dbg.caughtError = String(err && err.message ? err.message : err);
        setDiag(dbg);
      }
      setLoading(false);
    }
  }, [userID, userRole, showUnbilled, searchQuery]);

  useEffect(() => {
    fetchTickets();
    window.addEventListener("online", fetchTickets);
    return () => window.removeEventListener("online", fetchTickets);
  }, [fetchTickets]);
  
  useEffect(() => {
    if (hoursOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [hoursOpen]);

  /*─── HOURS: match TicketGrid behavior exactly (clear on range change) ───*/
  useEffect(() => {
    setHoursData([]);
    setHoursLoading(false);
  }, [startDate, endDate]);

  /*──────── hours calc (TicketGrid logic) ─────────*/
  // The HomePage list now loads in summary mode (no Items), so the Hours modal
  // pulls the full withItems payload on demand — only when the user taps "Run"
  // (and only roles A/O can open the modal). This keeps the heavy fetch off the
  // initial mobile load while preserving hours accuracy.
  const calcHours = useCallback(async () => {
    setHoursLoading(true);
    try {
      const { list } = await fetchTicketList(
        `${baseUrl}/api/tickets.php?withItems=1`
      );
      const fullTickets = Array.isArray(list)
        ? list.filter((t) => !t.isNextTicketID)
        : [];
      const src = fullTickets.length ? fullTickets : allTickets;

      const startD = new Date(`${startDate}T00:00:00`);
      const endD = new Date(`${endDate}T23:59:59`);
      const map = new Map();

      src.forEach((t) => {
        const td = new Date(`${t.TicketDate}T00:00:00`);
        if (td < startD || td > endD) return;

        t.Items?.forEach((it) => {
          if (it.UseStartStop !== "Y") return;
          const qty = parseFloat(it.Quantity) || 0;
          const id = String(it.ItemID ?? "");
          if (!map.has(id)) {
            map.set(id, {
              ItemID: id,
              ItemDescription: it.ItemDescription,
              HoursForDateRange: qty,
            });
          } else {
            map.get(id).HoursForDateRange += qty;
          }
        });
      });

      setHoursData(
        Array.from(map.values()).sort(
          (a, b) => b.HoursForDateRange - a.HoursForDateRange
        )
      );
    } catch (e) {
      setHoursData([]);
    } finally {
      setHoursLoading(false);
    }
  }, [allTickets, startDate, endDate]);

  /*──────── open a ticket's detail view ─────────*/
  // Summary list rows carry no Items. For a server ticket we fetch the full record
  // once (with Items) before navigating so the detail/edit view is unchanged;
  // offline/locally-created tickets already carry Items, so we pass them straight
  // through. On any fetch failure we fall back to the summary row (header still
  // renders) rather than blocking navigation.
  const openTicket = useCallback(
    async (t) => {
      let full = t;
      if (t && !t.Items && t.Ticket != null && navigator.onLine) {
        try {
          const res = await fetch(
            `${baseUrl}/api/tickets.php?ticket=${encodeURIComponent(t.Ticket)}`
          );
          if (res.ok) {
            const arr = await res.json();
            const found = Array.isArray(arr)
              ? arr.find((x) => x && !x.isNextTicketID)
              : arr && typeof arr === "object"
              ? arr
              : null;
            if (found && found.Items) full = found;
          }
        } catch (e) {
          /* fall back to summary row */
        }
      }
      navigate("/view-field-ticket", {
        state: { ticket: full, highestTicketNumber },
      });
    },
    [navigate, highestTicketNumber]
  );

  /*──────── pagination ─────────*/
  const pagedTickets = useMemo(() => {
    const start = (currentPage - 1) * ticketsPerPage;
    return tickets.slice(start, start + ticketsPerPage);
  }, [tickets, currentPage]);

  const pageCount = Math.max(1, Math.ceil(tickets.length / ticketsPerPage));

  // Self-heal a stale page index. hpCurrentPage is persisted in localStorage; if a
  // device kept a page number from a session with more tickets, pagedTickets would
  // slice to [] and the list looks empty even though `tickets` has data. This is a
  // per-device (localStorage) divergence — exactly the "desktop shows, phone blank"
  // shape. Clamp only fires when out of range, so desktop paging is unaffected.
  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(1);
      lsSet(LS.PAGE, "1");
    }
  }, [pageCount, currentPage]);

  // debugTickets=2: after render, inspect the ACTUAL ticket-list DOM so we can tell
  // "rows exist but are hidden by CSS" apart from "no rows rendered at all". This
  // proves/refutes a render/CSS cause that device emulation can't surface.
  useEffect(() => {
    if (DEBUG_TICKETS_LEVEL < 2 || loading) return;
    const el = listRef.current;
    if (!el || typeof window === "undefined") return;
    const cs = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    setDomDiag({
      renderedCards: el.childElementCount,
      display: cs.display,
      visibility: cs.visibility,
      opacity: cs.opacity,
      overflow: cs.overflow,
      heightPx: Math.round(rect.height),
      widthPx: Math.round(rect.width),
      offTop: Math.round(rect.top),
      offLeft: Math.round(rect.left),
    });
  }, [loading, tickets, currentPage]);

  // debugTickets=2: tap "Run raw ticket fetch test" to compare fetch option variants
  // directly on the real device. Sanitized output only — no secrets, no full payload.
  const runRawFetchTest = useCallback(async () => {
    const url = `${baseUrl}/api/tickets.php?withItems=1`;
    const variants = [
      ["app-default", undefined],
      ["no-store", { cache: "no-store" }],
      ["credentials:include", { credentials: "include" }],
      ["credentials:same-origin", { credentials: "same-origin" }],
    ];
    const out = [];
    for (const [label, opts] of variants) {
      try {
        const r = await fetch(url, opts);
        const ct = r.headers.get("content-type") || "";
        const text = await r.clone().text();
        let shape = "n/a";
        let count = null;
        try {
          const j = JSON.parse(text);
          shape = Array.isArray(j)
            ? `array(${j.length})`
            : `object{${Object.keys(j).slice(0, 8).join(",")}}`;
          const arr = Array.isArray(j)
            ? j
            : j.tickets || j.data || j.rows || null;
          if (Array.isArray(arr)) count = arr.length;
        } catch (e) {
          shape = "non-JSON";
        }
        out.push({
          variant: label,
          status: r.status,
          contentType: ct,
          redirected: r.redirected,
          finalUrl: r.url,
          textPrefix: text.slice(0, 120).replace(/\s+/g, " "),
          shape,
          count,
        });
      } catch (e) {
        out.push({ variant: label, error: String(e && e.message ? e.message : e) });
      }
    }
    setRawTest(out);
    // eslint-disable-next-line no-console
    console.log("[debugTickets raw-fetch]", out);
  }, [baseUrl]);

  /*───────────────────────────── JSX ─────────────────────────*/
  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* gradient bg */}
      <div
        className={`fixed inset-0 -z-10 opacity-50 ${
          theme === "dark"
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-gray-100 to-gray-200"
        }`}
      />

      {/* ── gated diagnostics (append ?debugTickets=1) — no secrets ── */}
      {DEBUG_TICKETS && (
        <div
          style={{
            position: "fixed",
            top: 8,
            left: 8,
            right: 8,
            zIndex: 9999,
            maxHeight: "45vh",
            overflow: "auto",
            background: "rgba(0,0,0,0.88)",
            color: "#0f0",
            font: "11px/1.45 monospace",
            padding: "10px 12px",
            borderRadius: 8,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <b style={{ color: "#9cf" }}>
            debugTickets={DEBUG_TICKETS_LEVEL} — ticket pipeline
          </b>
          {"\n"}
          {JSON.stringify(
            {
              ...(diag || { status: "fetching…" }),
              render: {
                loading,
                loadError: loadError || "(none)",
                ticketsInState: tickets.length,
                pageCount,
                currentPage,
                pagedShown: pagedTickets.length,
                writeAccess,
              },
              ...(DEBUG_TICKETS_LEVEL >= 2
                ? { domDiag: domDiag || "(rendering…)", rawFetchTest: rawTest }
                : {}),
            },
            null,
            2
          )}
          {DEBUG_TICKETS_LEVEL >= 2 && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={runRawFetchTest}
                style={{
                  background: "#0af",
                  color: "#000",
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  font: "bold 12px monospace",
                }}
              >
                Run raw ticket fetch test
              </button>
            </div>
          )}
        </div>
      )}

      {/*──────── Hours Modal ─────*/}
      <Modal open={hoursOpen} onClose={() => setHoursOpen(false)} theme={theme}>
        <div
          className={`
              w-full max-w-[720px] max-h-[80vh]
              rounded-3xl shadow-2xl ring-1 ring-black/10 backdrop-blur-md
              ${theme === "dark" ? "bg-gray-800/80 text-white" : "bg-white/80 text-gray-900"}
              flex flex-col overflow-hidden
            `}
        >

          {/* header */}
          <header
            className={`
        flex items-center justify-between px-6 py-4
        ${
          theme === "dark"
            ? "bg-gradient-to-r from-indigo-800/90 to-purple-900/90"
            : "bg-gradient-to-r from-indigo-500/90 to-purple-600/90"
        }
        text-white
      `}
          >
            <h2 className="text-xl font-bold tracking-wide">Hours by Item</h2>
            <button
              onClick={() => setHoursOpen(false)}
              className="p-2 transition hover:scale-110 focus:outline-none"
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} /> {/* add faTimes import */}
            </button>
          </header>

          {/* body */}
          <section className="flex-grow overflow-auto p-6 space-y-6">
            {/* quick links */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                ["Today", 0],
                ["Last 7 d", 6],
                ["MTD", new Date().getDate() - 1],
                ["Prev Month", "prev"],
                [
                  "YTD",
                  (() => {
                    const d = new Date();
                    return Math.floor(
                      (d - new Date(d.getFullYear(), 0, 1)) / 8.64e7
                    );
                  })(),
                ],
              ].map(([label, offset]) => (
                <button
                  key={label}
                  onClick={() => {
                    const now = new Date();
                    let s,
                      e = now;
                    if (offset === "prev") {
                      s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      e = new Date(now.getFullYear(), now.getMonth(), 0);
                    } else {
                      s = new Date(now);
                      s.setDate(now.getDate() - offset);
                    }
                    updateRange(toISO(s), toISO(e));
                  }}
                  className={`
              px-4 py-1.5 rounded-full font-medium text-sm shadow
              ring-1 ring-inset transition
              ${
                theme === "dark"
                  ? "bg-gray-700/60 hover:bg-gray-600/70 ring-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 ring-gray-300"
              }
            `}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* date pickers + run */}
            <div className="flex flex-wrap justify-center gap-4">
              {[
                ["Start", startDate, (v) => updateRange(v, endDate)],
                ["End", endDate, (v) => updateRange(startDate, v)],
              ].map(([label, val, onChange]) => (
                <label key={label} className="flex flex-col text-sm gap-1">
                  <span className="font-medium">{label}</span>
                  <input
                    type="date"
                    value={val}
                    onChange={(e) => onChange(e.target.value)}
                    className={`
                rounded-md px-3 py-1 shadow-inner
                ${theme === "dark" ? "bg-gray-700" : "bg-gray-200"}
              `}
                  />
                </label>
              ))}

              <button
                onClick={calcHours}
                className={`
            inline-flex items-center px-4 py-2 rounded-md font-semibold shadow
            transition hover:brightness-110
            ${
              theme === "dark"
                ? "bg-purple-700 text-white"
                : "bg-purple-600 text-white"
            }
          `}
              >
                Run
              </button>
            </div>

            {/* results */}
            <div className="max-h-60 overflow-y-auto rounded-lg shadow-inner">
              {hoursLoading ? (
                <p className="italic text-center py-10">Calculating…</p>
              ) : hoursData.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      className={`
                  text-left uppercase tracking-wider text-xs
                  ${theme === "dark" ? "text-gray-300" : "text-gray-500"}
                `}
                    >
                      <th className="py-2 pr-2">Item ID</th>
                      <th className="py-2 pr-2">Description</th>
                      <th className="py-2 text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursData.map((r, i) => (
                      <tr
                        key={r.ItemID}
                        className={i % 2 ? "bg-black/5 dark:bg-white/5" : ""}
                      >
                        <td className="py-1.5 pr-2">{r.ItemID}</td>
                        <td className="py-1.5 pr-2">{r.ItemDescription}</td>
                        <td className="py-1.5 text-right">
                          {(+r.HoursForDateRange).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="italic text-center py-10">
                  No data for selected range.
                </p>
              )}
            </div>
          </section>

          {/* footer */}
          <footer className="px-6 py-4">
            <button
              onClick={() => setHoursOpen(false)}
              className={`
          w-full rounded-md py-2 font-semibold shadow
          transition hover:brightness-110
          ${
            theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 text-gray-800"
          }
        `}
            >
              Close
            </button>
          </footer>
        </div>
      </Modal>

      {/*──────── Explorer Modal ──*/}
      {perms.hasOgFiles && (
        <ExplorerModal
          open={explorerOpen}
          onClose={() => setExplorerOpen(false)}
          theme={theme}
          companyName={companyName}
        />
      )}

      {/*──────── main card ───────*/}
      <div className="container mx-auto px-4 py-8 relative z-10">
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-24 w-24 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="flex flex-col items-center pt-10">
            <div
              className={`w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden ${
                theme === "dark"
                  ? "bg-gradient-to-r from-gray-800 to-gray-900"
                  : "bg-gradient-to-r from-white to-gray-100"
              }`}
            >
              {/* banner */}
              <div
                className={`p-6 text-center relative ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-blue-900 to-indigo-900"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500"
                }`}
              >
                <h2 className="text-4xl md:text-3xl font-bold mb-6 text-white">
                  Ticket Dashboard
                </h2>

                {/* hours */}
                {["A", "O"].includes(userRole) && (
                  <button
                    onClick={() => {
                      setHoursOpen(true);
                    }}
                    className="absolute top-4 left-4 text-white hover:text-gray-200 focus:outline-none"
                    title="Hours summary"
                  >
                    <FontAwesomeIcon icon={faClock} size="lg" />
                  </button>
                )}

                {/* explorer */}
                {perms.hasOgFiles && (
                  <button
                    onClick={() => setExplorerOpen(true)}
                    className="absolute top-4 left-14 text-white hover:text-gray-200 focus:outline-none"
                    title="Lease File Explorer"
                  >
                    <FontAwesomeIcon icon={faFolderOpen} size="lg" />
                  </button>
                )}

                {/* billed/unbilled */}
                {userRole !== "P" && (
                  <button
                    onClick={() =>
                      setShowUnbilled((prev) => {
                        const n = !prev;
                        lsSet(LS.SHOW_UNBILLED, JSON.stringify(n));
                        setCurrentPage(1);
                        return n;
                      })
                    }
                    className={`absolute top-4 right-4 hidden sm:inline-flex items-center font-bold py-2 px-4 rounded-full shadow-lg transform hover:scale-105 transition ${
                      theme === "dark"
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-white hover:bg-gray-100 text-blue-600"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={showUnbilled ? faEye : faEyeSlash}
                      className="mr-2"
                    />
                    {showUnbilled ? "Unbilled" : "Billed"}
                  </button>
                )}

                {/* search tickets */}
                <input
                  type="text"
                  ref={searchRef}
                  defaultValue={searchQuery}
                  onChange={(e) => debouncedSearchTickets(e.target.value)}
                  placeholder="Search tickets…"
                  className={`w-full max-w-lg mx-auto mb-6 px-4 py-2 rounded-lg shadow-md focus:ring-2 focus:ring-blue-500 ${
                    theme === "dark"
                      ? "bg-gray-800 text-white placeholder-gray-400"
                      : "bg-white text-gray-800 placeholder-gray-500"
                  }`}
                />

                {writeAccess && (
                  <button
                    onClick={() =>
                      navigate(`/create-field-ticket/${highestTicketNumber}`)
                    }
                    className={`inline-flex items-center font-bold py-3 px-6 rounded-full shadow-lg transform hover:scale-105 transition ${
                      theme === "dark"
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-white hover:bg-gray-100 text-blue-600"
                    }`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create Ticket
                  </button>
                )}
              </div>

              {/* load error (only when there's nothing to show) */}
              {loadError && tickets.length === 0 && (
                <p
                  className={`text-center text-sm py-4 ${
                    theme === "dark" ? "text-red-300" : "text-red-600"
                  }`}
                >
                  {loadError}
                </p>
              )}

              {/* search is hiding everything — offer a one-tap clear so a stale or
                  mistyped query can't look like "no tickets" (esp. on mobile). */}
              {!loadError &&
                searchQuery &&
                tickets.length === 0 &&
                preSearchCount > 0 && (
                  <div
                    className={`text-center text-sm py-4 ${
                      theme === "dark" ? "text-yellow-300" : "text-yellow-700"
                    }`}
                  >
                    A search filter is hiding {preSearchCount} ticket
                    {preSearchCount === 1 ? "" : "s"}.{" "}
                    <button
                      type="button"
                      className="underline font-semibold"
                      onClick={() => {
                        if (searchRef.current) searchRef.current.value = "";
                        setSearchQuery("");
                        lsSet(LS.QUICK_FILTER, "");
                        setCurrentPage(1);
                        lsSet(LS.PAGE, "1");
                      }}
                    >
                      Clear search
                    </button>
                  </div>
                )}

              {/* list */}
              <Suspense fallback={<div className="p-4">Loading…</div>}>
                <ul
                  ref={listRef}
                  className={`divide-y ${
                    theme === "dark" ? "divide-gray-700" : "divide-gray-300"
                  }`}
                >
                  {pagedTickets.map((t) => (
                    <TicketItem
                      key={t.Ticket}
                      ticket={t}
                      theme={theme}
                      searchQuery={searchQuery}
                      onClick={() => openTicket(t)}
                    />
                  ))}
                </ul>
              </Suspense>

              {/* pagination */}
              <div className="py-6 flex flex-wrap justify-center gap-2">
                {Array.from({ length: pageCount }).map((_, idx) => {
                  const page = idx + 1;
                  const active = page === currentPage;
                  return (
                    <button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                        lsSet(LS.PAGE, String(page));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full shadow transition ${
                        active
                          ? theme === "dark"
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : theme === "dark"
                          ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(HomePage);
